/**
 * ч.1357: статистична звітність до Держстату — 1-опт (квартальна, S0601318) і
 * 10-зез (квартальна, S1001017). Заповнення ручне за бланком; подання — напряму у
 * приймальний шлюз Держстату (підпис і шифрування у браузері), квитанції.
 */
import { api } from './client';
import type { Page } from './catalogs';

export type StatFormCode = 'S0601318' | 'S1001017';
export type StatFormSlug = '1-opt' | '10-zez';
export const FORM_BY_SLUG: Record<StatFormSlug, StatFormCode> = { '1-opt': 'S0601318', '10-zez': 'S1001017' };
export const SLUG_BY_FORM: Record<StatFormCode, StatFormSlug> = { S0601318: '1-opt', S1001017: '10-zez' };
export const FORM_SHORT: Record<StatFormCode, string> = { S0601318: '1-опт (квартальна)', S1001017: '10-зез (квартальна)' };
export const FORM_TITLE: Record<StatFormCode, string> = {
  S0601318: 'Звіт про продаж і запаси товарів (продукції) в оптовій торгівлі',
  S1001017: 'Звіт підприємства з іноземними інвестиціями',
};
export const QUARTER_LABELS: Record<number, string> = { 1: 'I квартал', 2: 'II квартал', 3: 'III квартал', 4: 'IV квартал' };

export interface StatReason { code: number | null; key: string; label: string; forms: string[] }
export interface OptColumn { col: number; label: string; kind: 'natural' | 'money' }
export interface OptRow {
  code: string; label: string; unit: string; level: number; note: string | null;
  cols: number[]; integer: boolean; section: 'food' | 'nonfood';
}
export interface OptControl { kind: 'eq' | 'ge'; row: string; rows: string[]; cols: number[]; n: string }
export interface ZezRow {
  section: 'I' | 'I-share' | 'II'; code: string; label: string; bold?: boolean; level?: number;
  formula?: string; signed?: boolean;
}
export interface ZezSection { key: 'I' | 'I-share' | 'II'; label: string; unit: string }
export interface StatCountry { code: string; name: string }
export interface StatFormDefinition {
  form_code: StatFormCode; title: string; short: string; slug: StatFormSlug; deadline_day: number;
  reasons: StatReason[];
  columns?: OptColumn[]; rows: (OptRow | ZezRow)[]; sections: ({ key: string; label: string; total?: string; unit?: string })[];
  controls?: OptControl[];
  section3_rows?: string[]; questions?: { code: string; label: string }[]; countries?: StatCountry[];
  signed_rows?: string[];
}
export interface GoodsHit { code: string; name: string; matched: string; rows: string[]; row_labels: string[]; units: string[] }

export interface StatCountryBlock { name: string; code: string; values: Record<string, string> }
export interface StatFields {
  ruk?: string; tel?: string; email?: string; katottg_fact?: string; actual_address?: string;
  zero_zvit?: boolean; reason_key?: string | null;
}
export interface StatReport {
  id: string; organization_id: string; document_number: string; document_date: string;
  form_code: StatFormCode; period_year: number; period_quarter: number; doc_cnt: number; doc_type: number;
  values: Record<string, string>; countries: StatCountryBlock[]; fields: StatFields;
  comment: string | null; deletion_mark: boolean; created_at: string; updated_at: string;
}
export interface StatCheck {
  errors: { form: string; message: string }[]; warnings: { form: string; message: string }[];
  form_code: string; file_name?: string; missing?: string[];
}
export type StatSubStatus = 'DRAFT' | 'SIGNED' | 'SENT' | 'DELIVERED' | 'ACCEPTED' | 'REJECTED' | 'ERROR';
export interface StatSubmission {
  id: string; organization_id: string; report_id: string; form_code: StatFormCode; file_name: string;
  status: StatSubStatus; signer_edrpou: string | null; sender_email: string | null;
  gate_result: string | null; error_message: string | null;
  kvt1_fname: string | null; kvt1_xml: string | null; kvt2_fname: string | null; kvt2_xml: string | null;
  notice_xml: string | null; receipt_text: string | null; receipt_accepted: boolean | null;
  signed_at: string | null; sent_at: string | null; delivered_at: string | null; finalized_at: string | null;
  created_at: string;
}
export interface StatMessage {
  id: string; organization_id: string; gate_msg_id: string; file_name: string | null; body_base64: string;
  received_at: string; processed: boolean; submission_id: string | null; kind: string | null;
}
export interface StatSubmitResult { submission: StatSubmission; gate_result: string | null; message: string | null }

export const SUB_STATUS_LABEL: Record<StatSubStatus, string> = {
  DRAFT: 'чернетка', SIGNED: 'підписано', SENT: 'відправлено', DELIVERED: 'доставлено (квитанція №1)',
  ACCEPTED: 'прийнято (квитанція №2)', REJECTED: 'не прийнято', ERROR: 'помилка шлюзу',
};
export const SUB_STATUS_COLOR: Record<StatSubStatus, string> = {
  DRAFT: 'var(--muted)', SIGNED: 'var(--badge-blue-fg)', SENT: 'var(--badge-blue-fg)',
  DELIVERED: 'var(--badge-blue-fg)', ACCEPTED: 'var(--badge-green-fg)', REJECTED: 'var(--badge-red-fg)',
  ERROR: 'var(--badge-red-fg)',
};

const BASE = '/stat-reports';
export const statReports = {
  form: (code: StatFormCode) => api.get<StatFormDefinition>(`${BASE}/forms/${code}`).then((r) => r.data),
  goods: (q: string) => api.get<{ items: GoodsHit[] }>(`${BASE}/forms/S0601318/goods`, { params: { q } }).then((r) => r.data),
  countries: () => api.get<{ items: StatCountry[] }>(`${BASE}/countries`).then((r) => r.data),
  list: (params?: {
    organization_id?: string; form_code?: StatFormCode; period_year?: number; period_quarter?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) => api.get<Page<StatReport>>(BASE, { params }).then((r) => r.data),
  get: (id: string) => api.get<StatReport>(`${BASE}/${id}`).then((r) => r.data),
  create: (p: { organization_id: string; form_code: StatFormCode; period_year: number; period_quarter: number; doc_type?: number }) =>
    api.post<StatReport>(BASE, p).then((r) => r.data),
  update: (id: string, p: {
    document_date?: string; doc_cnt?: number; doc_type?: number; values?: Record<string, string>;
    countries?: StatCountryBlock[]; fields?: StatFields; comment?: string | null;
  }) => api.patch<StatReport>(`${BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`${BASE}/${id}`).then(() => undefined),
  check: (id: string) => api.get<StatCheck>(`${BASE}/${id}/check`).then((r) => r.data),
  files: (id: string) => api.get<{ files: { fname: string; xml_base64: string }[]; sender_email: string }>(`${BASE}/${id}/files`).then((r) => r.data),
  xmlUrl: (id: string) => `/api/v1${BASE}/${id}/xml`,
  submission: (id: string) => api.get<{ submission_id: string | null; status: StatSubStatus | null; receipt_accepted: boolean | null;
    receipt_text: string | null; error_message: string | null; gate_result: string | null; sent_at: string | null }>(`${BASE}/${id}/submission`).then((r) => r.data),
};

const SUB = '/stat-submissions';
export const statSubmissions = {
  list: (params?: { organization_id?: string; report_id?: string; limit?: number; offset?: number }) =>
    api.get<Page<StatSubmission>>(SUB, { params }).then((r) => r.data),
  get: (id: string) => api.get<StatSubmission>(`${SUB}/${id}`).then((r) => r.data),
  submit: (p: { organization_id: string; report_id: string; file_name: string; container_base64: string;
    signer_edrpou?: string; sender_email: string }) =>
    api.post<StatSubmitResult>(SUB, p).then((r) => r.data),
  sign: (id: string, p: { file_name: string; container_base64: string; signer_edrpou?: string; sender_email?: string }) =>
    api.post<StatSubmission>(`${SUB}/${id}/sign`, p).then((r) => r.data),
  send: (id: string) => api.post<StatSubmitResult>(`${SUB}/${id}/send`).then((r) => r.data),
  receive: (p: { organization_id: string; signed_email_base64: string }) =>
    api.post<Page<StatMessage>>(`${SUB}/receive`, p).then((r) => r.data),
  messages: (params?: { organization_id?: string; unprocessed?: boolean; limit?: number }) =>
    api.get<Page<StatMessage>>(`${SUB}/messages`, { params }).then((r) => r.data),
  decrypted: (msgId: string, xml: string) =>
    api.post<{ message: StatMessage; submission: StatSubmission | null; kind: string | null; accepted: boolean | null; text: string; file_name: string | null }>(
      `${SUB}/messages/${msgId}/decrypted`, { xml }).then((r) => r.data),
  deleteMessages: (p: { organization_id: string; ids: string[]; signed_ids_base64: string }) =>
    api.post<{ deleted: number; gate_result: string }>(`${SUB}/messages/delete`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`${SUB}/${id}`).then(() => undefined),
};

/** Попередній завершений квартал (звіт подають після кварталу). */
export function previousQuarter(now: Date): { year: number; quarter: number } {
  const q = Math.floor(now.getMonth() / 3) + 1;
  return q === 1 ? { year: now.getFullYear() - 1, quarter: 4 } : { year: now.getFullYear(), quarter: q - 1 };
}

/** Підсумкові рядки 1-опт за контролями «=» (299, 206, 399): сума частин по грошових графах. */
export function optTotals(values: Record<string, string>, controls: OptControl[]): Record<string, string> {
  const out = { ...values };
  const num = (k: string) => { const n = Number(String(out[k] ?? '').replace(',', '.')); return Number.isFinite(n) ? n : 0; };
  // спершу вкладені (206), потім розділи (299/399) — порядок контролів у бланку
  const ordered = [...controls.filter((c) => c.kind === 'eq')].sort((a, b) => (a.row === '206' ? -1 : b.row === '206' ? 1 : 0));
  for (const c of ordered) {
    for (const col of c.cols) {
      const s = c.rows.reduce((acc, r) => acc + num(`A${r}_${col}`), 0);
      const any = c.rows.some((r) => out[`A${r}_${col}`] != null && out[`A${r}_${col}`] !== '');
      if (any) out[`A${c.row}_${col}`] = String(Math.round(s * 10) / 10);
    }
  }
  return out;
}
